import React, { Ref, useRef, RefCallback } from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { t } from "@impero-com/common/utils/i18n";
import { mergeRefs } from "@impero-com/common/utils/mergeRefs";
import { set } from "@impero-com/common/utils/obj";
import { FormField, FormLabel } from "@impero-com/ui";
import { InputNumber, Select } from "antd";
import { cx } from "class-variance-authority";

import * as theme from "./theme.css";

export interface AllocatedTime {
    value: number;
    unit: AllocatedTimeUnit;
}

export type AllocatedTimeUnit = "Days" | "Hours";

export interface AllocatedTimeInputProps extends AllocatedTimeInputBaseProps {
    value?: AllocatedTime;
    // Callback to update the value
    onChange: (update: (oldValue: AllocatedTime) => AllocatedTime) => void;
    error?: string | undefined;
    // Ref to the input number element
    fieldRef?: RefCallback<HTMLInputElement>;
    // Callback when the input loses focus or the select changes
    onBlur?: () => void;
    // Maximum value for the input, may vary depending on the unit
    max?: number;
}

export interface AllocatedTimeInputBaseProps {
    label?: string;
    helpText?: string;
    disabled?: boolean;
    // Disables the unit selection.
    // Note: value.unit must be defined if this is true
    disableChangeUnit?: boolean;
    // A React Node displayed after the input
    nodeAfter?: React.ReactNode;
}

export function AllocatedTimeInput({
    value,
    onChange,
    error,
    label,
    helpText,
    disabled,
    disableChangeUnit,
    nodeAfter,
    fieldRef,
    onBlur,
    max,
}: AllocatedTimeInputProps) {
    const inputNumberRef: Ref<HTMLInputElement> = useRef(null);

    if (disableChangeUnit && value?.unit === undefined) {
        throw new Error(
            "AllocatedTimeInput: disableChangeUnit is true, but value.unit is undefined"
        );
    }

    const addonAfter =
        disabled || disableChangeUnit ? (
            (value?.unit ?? "")
        ) : (
            <Select
                value={value?.unit}
                onChange={(unit) => {
                    onChange((oldValue) =>
                        set(oldValue, {
                            unit,
                        })
                    );
                }}
                onBlur={onBlur}
                dropdownMatchSelectWidth={false}
                options={[
                    {
                        label: t("AllocatedTime_Unit_Days"),
                        value: "Days",
                    },
                    {
                        label: t("AllocatedTime_Unit_Hours"),
                        value: "Hours",
                    },
                ]}
            />
        );

    const handlePressEnter = () => {
        inputNumberRef.current?.blur();
    };

    return (
        <FormField
            error={error}
            className={cx(error && ERRORED_CLASSNAME, theme.positionRelative)}
            lite={label === undefined}
        >
            {label && (
                <FormLabel
                    onClick={() => inputNumberRef.current?.focus()}
                    helpText={helpText}
                >
                    {label}
                </FormLabel>
            )}
            <div className={theme.inputWithText}>
                <InputNumber
                    ref={mergeRefs(inputNumberRef, fieldRef)}
                    value={value?.value}
                    className={theme.inputNumber}
                    min={0}
                    max={max}
                    // `value` is always an integer
                    precision={0}
                    disabled={disabled}
                    onChange={(value) =>
                        onChange((oldValue) =>
                            set(oldValue, {
                                value: value ?? 0,
                            })
                        )
                    }
                    onBlur={onBlur}
                    onPressEnter={handlePressEnter}
                    addonAfter={addonAfter}
                    status={error ? "error" : undefined}
                />
                {nodeAfter}
            </div>
        </FormField>
    );
}
